import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import TimelineEventType from "@app/scene/dashboard/enum/TimelineEventType";
import TimelineViewFilter from "@app/scene/dashboard/enum/TimelineViewFilter";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { FieldInsightsActions } from "@app/store/fieldInsights/actions";
import CardConfiguration, { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Dashboard from "@app/types/dashboard/Dashboard";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";
import TimelineEvent from "@app/types/events/TimelineEvent";

import OverrideDashboardsFormValues from "../types/OverrideDashboardsFormValues";

import { ActivitiesLoggedCardActions } from "./cards/activitiesLogged/actions";
import { ActivitiesOverdueCardActions } from "./cards/activitiesOverdue/actions";
import { AverageStopsPerRouteCardActions } from "./cards/averageStopsPerRoute/actions";
import { CheckInsCardActions } from "./cards/checkIns/actions";
import { ClosedStageDealAmountCardActions } from "./cards/closedStageDealAmount/actions";
import { ClosedStageDealCountCardActions } from "./cards/closedStageDealCount/actions";
import { CustomerFaceTimeCardActions } from "./cards/customerFaceTime/actions";
import { DistanceTravelledCardActions } from "./cards/distanceTravelled/actions";
import { EfficiencyMapCardActions } from "./cards/efficiencyMap/actions";
import { EfficiencyTrackerCardActions } from "./cards/efficiencyTracker/actions";
import { FieldTimeCardActions } from "./cards/fieldTime/actions";
import { FrequencyCoverageCardActions } from "./cards/frequencyCoverage/actions";
import { FrequencyDuringActivityCardActions } from "./cards/frequencyDuringActivity/actions";
import { MileageTrackingCardActions } from "./cards/mileageTracking/actions";
import { NewRecordsCardActions } from "./cards/newRecords/actions";
import { NewRoutesCardActions } from "./cards/newRoutes/actions";
import { NoContactInCardActions } from "./cards/noContactIn/actions";
import { NoteCompletionCardActions } from "./cards/noteCompletion/actions";
import { OpenStageDealAmountCardActions } from "./cards/openStageDealAmount/actions";
import { OutOfFrequencyCardActions } from "./cards/outOfFrequency/actions";
import { RecordsCadenceEffectivenessCardActions } from "./cards/recordsCadenceEffectiveness/actions";
import { RecordsPastDueCardActions } from "./cards/recordsPastDue/actions";
import { SkippedRecordsCardActions } from "./cards/skippedRecords/actions";
import { UpcomingActivitiesCardActions } from "./cards/upcomingActivities/actions";
import { CoachingNoteCategoriesActions } from "./coaching/categories/actions";
import { CoachingFilesActions } from "./coaching/files/actions";
import { CoachingNotesActions } from "./coaching/notes/actions";
import { CoachingNoteWithTaskActions } from "./coaching/noteWithTask/actions";
import { CoachingTasksActions } from "./coaching/tasks/actions";
import { StackRankActions } from "./stackRank/actions";
import { WeeklySummaryActions } from "./weeklySummary/actions";
import { WordCloudActions } from "./wordCloud/actions";

export * from "./cards/activitiesLogged/actions";
export * from "./cards/activitiesOverdue/actions";
export * from "./cards/averageStopsPerRoute/actions";
export * from "./cards/checkIns/actions";
export * from "./cards/closedStageDealAmount/actions";
export * from "./cards/closedStageDealCount/actions";
export * from "./cards/customerFaceTime/actions";
export * from "./cards/distanceTravelled/actions";
export * from "./cards/efficiencyMap/actions";
export * from "./cards/efficiencyTracker/actions";
export * from "./cards/fieldTime/actions";
export * from "./cards/frequencyCoverage/actions";
export * from "./cards/frequencyDuringActivity/actions";
export * from "./cards/mileageTracking/actions";
export * from "./cards/newRecords/actions";
export * from "./cards/newRoutes/actions";
export * from "./cards/noContactIn/actions";
export * from "./cards/noteCompletion/actions";
export * from "./cards/openStageDealAmount/actions";
export * from "./cards/outOfFrequency/actions";
export * from "./cards/recordsCadenceEffectiveness/actions";
export * from "./cards/recordsPastDue/actions";
export * from "./cards/skippedRecords/actions";
export * from "./cards/upcomingActivities/actions";
export * from "./coaching/categories/actions";
export * from "./coaching/files/actions";
export * from "./coaching/notes/actions";
export * from "./coaching/noteWithTask/actions";
export * from "./coaching/tasks/actions";
export * from "./stackRank/actions";
export * from "./weeklySummary/actions";
export * from "./wordCloud/actions";

export const changeScope = createAsyncAction(
  "@dashboard/changeScope/request",
  "@dashboard/changeScope/success",
  "@dashboard/changeScope/failure"
)<
  { scope: Scope; view: ViewType },
  { boards?: Dashboard[]; events?: TimelineEvent[]; oldestEventDate?: Date; view: ViewType },
  void
>();

export const fetchTimelineEvents = createAsyncAction(
  "@dashboard/fetchTimelineEvents/request",
  "@dashboard/fetchTimelineEvents/success",
  "@dashboard/fetchTimelineEvents/failure"
)<
  {
    eventTypes?: TimelineEventType[];
    onComplete?: () => void;
    query?: string;
    scope: Scope;
    startDate?: Date;
    viewFilter?: TimelineViewFilter;
  },
  { append?: boolean; events: TimelineEvent[]; hasMore?: boolean; total?: number },
  unknown
>();

// Drops the resolved legend colors so the whole list falls back to entity-type colors together,
// rather than leaving the page whose /pins call failed a different color from the pages around it.
export const clearTimelineEventsPinColor = createAction(
  "@dashboard/clearTimelineEventsPinColor"
)<void>();

export const addCard = createAsyncAction(
  "@dashboard/addCard/request",
  "@dashboard/addCard/success",
  "@dashboard/addCard/failure"
)<{ card: DashboardCard; scope: Scope }, Dashboard, void>();

export const saveCard = createAsyncAction(
  "@dashboard/saveCard/request",
  "@dashboard/saveCard/success",
  "@dashboard/saveCard/failure"
)<
  {
    cardId: DashboardCard["id"];
    configuration: CardConfiguration<any, any, any>;
    fullWidth?: DashboardCard["fullWidth"];
    scope: Scope;
    teamGoalConfiguration?: GoalConfiguration;
    updateGoalOverrides: boolean;
    updateMetaData?: Pick<DashboardCard, "updatedAt" | "updatedBy">;
    updatePreferencesOverrides: boolean;
  },
  Dashboard[],
  void
>();

export const saveDashboardLayout = createAsyncAction(
  "@dashboard/saveDashboardLayout/request",
  "@dashboard/saveDashboardLayout/success",
  "@dashboard/saveDashboardLayout/failure"
)<
  { cardPositions: Record<DashboardCard["id"], Pick<DashboardCard, "x" | "y">>; scope: Scope },
  Dashboard,
  void
>();

export const saveDashboardSettings = createAsyncAction(
  "@dashboard/saveDashboardSettings/request",
  "@dashboard/saveDashboardSettings/success",
  "@dashboard/saveDashboardSettings/failure"
)<
  {
    scope: Scope;
    settings: Partial<Omit<Dashboard["settings"], "cards" | "overrides">>;
  },
  Dashboard,
  void
>();

export const overrideTeamDashboards = createAsyncAction(
  "@dashboard/overrideTeamDashboards/request",
  "@dashboard/overrideTeamDashboards/success",
  "@dashboard/overrideTeamDashboards/failure"
)<OverrideDashboardsFormValues, Dashboard[], void>();

export const removeCard = createAsyncAction(
  "@dashboard/removeCard/request",
  "@dashboard/removeCard/success",
  "@dashboard/removeCard/failure"
)<{ cardId: DashboardCard["id"]; scope: Scope }, Dashboard[], void>();

export type Actions =
  | ActionType<
      | typeof addCard
      | typeof changeScope
      | typeof clearTimelineEventsPinColor
      | typeof fetchTimelineEvents
      | typeof overrideTeamDashboards
      | typeof removeCard
      | typeof saveCard
      | typeof saveDashboardLayout
      | typeof saveDashboardSettings
    >
  | ActivitiesLoggedCardActions
  | ActivitiesOverdueCardActions
  | AverageStopsPerRouteCardActions
  | CheckInsCardActions
  | ClosedStageDealAmountCardActions
  | ClosedStageDealCountCardActions
  | CoachingFilesActions
  | CoachingNoteCategoriesActions
  | CoachingNotesActions
  | CoachingNoteWithTaskActions
  | CoachingTasksActions
  | CustomerFaceTimeCardActions
  | DistanceTravelledCardActions
  | EfficiencyMapCardActions
  | EfficiencyTrackerCardActions
  | FieldInsightsActions
  | FieldTimeCardActions
  | FrequencyCoverageCardActions
  | FrequencyDuringActivityCardActions
  | MileageTrackingCardActions
  | NewRecordsCardActions
  | NewRoutesCardActions
  | NoContactInCardActions
  | NoteCompletionCardActions
  | OpenStageDealAmountCardActions
  | OutOfFrequencyCardActions
  | RecordsCadenceEffectivenessCardActions
  | RecordsPastDueCardActions
  | SkippedRecordsCardActions
  | StackRankActions
  | UpcomingActivitiesCardActions
  | WeeklySummaryActions
  | WordCloudActions;
